import {
  Alpha3CountryCode,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  Label,
  openDialog,
  Separator,
} from 'platform/components';
import {Box, Grid, GridItem, HStack, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isFalse, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomersV2Query} from '@dms/api/customer';
import {useAccM2StrediskoGetCentresQuery} from '@dms/api/finance';
import {
  PatchServiceOrderApiArg,
  PatchServiceOrderInsuranceApiArg,
  useGetInsuranceTypesQuery,
  useGetServiceOrderGroupsForServiceOrderQuery,
  useGetServiceOrderInsuranceQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantAccountingQuery,
  useGetServiceOrderVariantReportingQuery,
  usePatchServiceOrderInsuranceMutation,
  usePatchServiceOrderMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, PredefinedNotes, usePhoneNumbers, useTenant} from '@dms/shared';
import {FuelLevelInput} from '@dms/shared-aftersales';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {MileageValidationDialog} from '../../../components/MileageValidationDialog';
import {relativeDates} from '../../../constants/relativeDates';
import {useOrderMandatoryFields} from '../hooks/useOrderMandatoryFields';
import {OrderDetailFormRef} from '../types/orderDetailFormRef';
import {orderInsuranceFormSchema, OrderInsuranceFormType} from '../types/orderInsuranceFormSchema';
import {getCompletionAt} from '../utils/getCompletionAt';
import {getDefaultCompletionAtTime} from '../utils/getDefaultCompletionAtTime';
import {hasObjectNonEmptyValue} from '../utils/hasObjectNonEmptyValue';
import {DeactivateContractDialog} from './DeactivateContractDialog';
import {DriverInformationSection} from './DriverInformationSection';

interface OrderInsuranceFormProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
}

export const OrderInsuranceForm = forwardRef<OrderDetailFormRef, OrderInsuranceFormProps>(
  (props, ref) => {
    const [phoneInputKey, setPhoneInputKey] = useState(0);
    const refreshPhoneInputKey = () => setPhoneInputKey((prev) => prev + 1);
    const {
      data: serviceOrderInsurance,
      isFetching: isServiceOrderInsuranceFetching,
      isError: isServiceOrderInsuranceError,
    } = useGetServiceOrderInsuranceQuery(props);
    const {
      data: serviceOrder,
      isFetching: isServiceOrderFetching,
      isError: isServiceOrderError,
    } = useGetServiceOrderQuery(props);
    const {data: insuranceCompanies, isLoading: isInsuranceCompaniesLoading} =
      useGetCustomersV2Query({
        institutions: ['INSURANCE'],
      });
    const {
      isFieldRequired,
      isLoading: isServiceOrderVariantMandatoryFieldsLoading,
      isError: isServiceOrderVariantMandatoryFieldsError,
    } = useOrderMandatoryFields(props.serviceOrderId);
    const {tenantCurrency} = useTenant();
    const formatCurrencySymbol = useCurrencySymbolFormatter();

    const {countriesOptions} = usePhoneNumbers();

    const {
      data: orderVariantAccounting,
      isFetching: isOrderVariantAccountingFetching,
      isError: isOrderVariantAccountingError,
    } = useGetServiceOrderVariantAccountingQuery(
      {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
      {skip: isNilOrEmpty(serviceOrder)}
    );

    const {
      data: orderVariantReporting,
      isFetching: isOrderVariantReportingFetching,
      isError: isOrderVariantReportingError,
    } = useGetServiceOrderVariantReportingQuery(
      {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
      {skip: isNilOrEmpty(serviceOrder)}
    );

    const {
      data: serviceCentres,
      isLoading: isServiceCentresLoading,
      isError: isServiceCentresError,
    } = useAccM2StrediskoGetCentresQuery();

    const {
      data: orderGroups,
      isLoading: isOrderGroupsLoading,
      isError: isOrderGroupsError,
    } = useGetServiceOrderGroupsForServiceOrderQuery({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    });
    const {
      data: insuranceTypes,
      isLoading: isInsuranceTypesLoading,
      isError: isInsuranceTypesError,
    } = useGetInsuranceTypesQuery();

    const [patchServiceOrder] = usePatchServiceOrderMutation();
    const [patchServiceOrderInsurance] = usePatchServiceOrderInsuranceMutation();
    const formApiRef = useRef<UseFormReturn<OrderInsuranceFormType>>(null);

    useImperativeHandle(ref, () => ({
      submit: async () => {
        if (formApiRef.current) {
          const isValid = await formApiRef.current.trigger();
          if (isValid) {
            const values = formApiRef.current.getValues();
            await onSubmit(values);
          }
        }
      },
    }));

    const handleSaveData = async (
      serviceOrderData: PatchServiceOrderApiArg['body'],
      orderInsuranceData: PatchServiceOrderInsuranceApiArg['body']
    ) => {
      if (isNil(serviceOrderData) || isNil(orderInsuranceData)) {
        return;
      }

      const patchServiceOrderMutation = hasObjectNonEmptyValue(serviceOrderData)
        ? patchServiceOrder({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
            body: serviceOrderData,
          })
            .unwrap()
            .catch(handleApiError)
        : null;
      const patchServiceOrderInsuranceMutation = hasObjectNonEmptyValue(orderInsuranceData)
        ? patchServiceOrderInsurance({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
            body: orderInsuranceData,
          })
            .unwrap()
            .catch(handleApiError)
        : null;
      await Promise.all([patchServiceOrderMutation, patchServiceOrderInsuranceMutation]).then(
        ([orderResponse]) => {
          if (isFalse(orderResponse?.isMileageValid)) {
            openDialog(
              <MileageValidationDialog
                highestMileage={orderResponse?.highestMileage}
                data-testid={suffixTestId('mileageValidation', props)}
              />,
              {size: 'small'}
            );
          }
        }
      );
    };

    const onSubmit = async (data: OrderInsuranceFormType) => {
      const serviceOrderData: PatchServiceOrderApiArg['body'] = {
        completionAt: getCompletionAt(data.completionAtDate, data.completionAtTime),
        recipientId: serviceOrder?.recipientId,
        technicanId: serviceOrder?.technicanId,
        totalPriceEstimated: data.totalPriceEstimated,
        note: data.note,
        serviceCenterId: data.serviceCenterId,
        serviceOrderGroupId: data.serviceOrderGroupId,
        contactInformation: {
          id: data.driverId,
          name: data.driverFullName,
          email: data.driverEmail,
          phoneNumber: data.driverPhone,
          identityCard: data.driverIdentityCard,
        },
        mileage: data.mileage,
        fuelTank: (data.fuelTank ?? 0) / 100,
        cancelReason: data.cancelReason,
      };

      const orderInsuranceData: PatchServiceOrderInsuranceApiArg['body'] = {
        coverLetter: data.coverLetter,
        initialInspectionPerformed: data.initialInspectionPerformed,
        insuranceClaimNumber: data.insuranceClaimNumber,
        insuranceCompany: data.insuranceCompany,
        insuranceEventNumber: data.insuranceEventNumber,
        insuranceTypeId: data.insuranceTypeId,
        returnUsedPart: data.returnUsedPart,
        unblockingOfPayment: data.unblockingOfPayment,
        vatDeducatible: data.vatDeducatible,
        insuranceClaimOccurrenceAt: data.insuranceClaimOccurrenceAt,
      };

      await match([serviceOrderInsurance?.insuranceCompany, orderInsuranceData.insuranceCompany])
        .when(
          ([oldValue, newValue]) => isNotNilOrEmpty(oldValue) && isNilOrEmpty(newValue),
          () =>
            openDialog(
              <DeactivateContractDialog
                onClose={closeCurrentDialog}
                onDeactivate={(isDiscountRemove) =>
                  handleSaveData(serviceOrderData, {...orderInsuranceData, isDiscountRemove})
                }
                data-testid={suffixTestId('deactivateDialog', props)}
              />,
              {title: i18n.t('entity.order.labels.deactivateContract'), size: 'small'}
            )
        )
        .when(
          ([oldValue, newValue]) =>
            isNotNilOrEmpty(oldValue) && isNotNilOrEmpty(newValue) && oldValue !== newValue,
          () =>
            openDialog(
              <DeactivateContractDialog
                onClose={closeCurrentDialog}
                onDeactivate={(isDiscountApply) =>
                  handleSaveData(serviceOrderData, {...orderInsuranceData, isDiscountApply})
                }
                data-testid={suffixTestId('deactivateDialog', props)}
              />,
              {title: i18n.t('entity.order.labels.deactivateContract'), size: 'small'}
            )
        )
        .otherwise(() => handleSaveData(serviceOrderData, orderInsuranceData));
    };

    // TODO: T20-26316
    const insuranceCompaniesOptions =
      insuranceCompanies?.map((insurance) => ({
        value: insurance.id,
        label: insurance.businessInfo?.businessInfo?.tradeName,
      })) ?? [];

    const filteredServiceCentresOptions =
      serviceCentres?.m2centres
        ?.filter(
          (center) =>
            !orderVariantAccounting?.excludedServiceCenterIds?.includes(center?.code ?? '')
        )
        .map((center) => ({
          label: center?.name,
          value: center?.code,
        })) ?? [];

    const filteredOrderGroupsOptions =
      orderGroups?.map((group) => ({
        label: group?.name,
        value: group?.serviceOrderGroupId,
      })) ?? [];

    const insuranceTypesOptions =
      insuranceTypes
        ?.filter((insuranceType) => insuranceType?.isActive)
        ?.map((insuranceType) => ({
          label: insuranceType?.name,
          value: insuranceType?.insuranceTypeId,
        })) ?? [];

    const defaultInsuranceType = insuranceTypes?.find((insuranceType) => insuranceType?.isDefault);

    const defaultValues: Partial<OrderInsuranceFormType> = {
      completionAtDate: serviceOrder?.completionAt
        ? parseDate(serviceOrder?.completionAt)
        : undefined,
      completionAtTime: getDefaultCompletionAtTime(serviceOrder?.completionAt),
      note: serviceOrder?.note,
      totalPriceEstimated: serviceOrder?.totalPriceEstimated?.amount,
      coverLetter: serviceOrderInsurance?.coverLetter,
      initialInspectionPerformed: serviceOrderInsurance?.initialInspectionPerformed,
      insuranceClaimNumber: serviceOrderInsurance?.insuranceClaimNumber,
      insuranceCompany: serviceOrderInsurance?.insuranceCompany,
      insuranceEventNumber: serviceOrderInsurance?.insuranceEventNumber,
      insuranceTypeId:
        serviceOrderInsurance?.insuranceTypeId ?? defaultInsuranceType?.insuranceTypeId,
      returnUsedPart: serviceOrderInsurance?.returnUsedPart,
      unblockingOfPayment: serviceOrderInsurance?.unblockingOfPayment,
      vatDeducatible: serviceOrderInsurance?.vatDeducatible,
      contactPerson: serviceOrder?.contactInformation?.id,
      email: serviceOrder?.contactInformation?.email,
      phone: serviceOrder?.contactInformation?.phoneNumber?.number,
      mileage: serviceOrder?.mileage,
      fuelTank: Math.round((serviceOrder?.fuelTank ?? 0) * 100),
      serviceOrderGroupId:
        serviceOrder?.serviceOrderGroupId || orderVariantReporting?.defaultServiceOrderGroupId,
      serviceCenterId:
        serviceOrder?.serviceCenterId || orderVariantAccounting?.defaultServiceCenterId,
      insuranceClaimOccurrenceAt: serviceOrderInsurance?.insuranceClaimOccurrenceAt,
      driverId: serviceOrder?.contactInformation?.id,
      driverFullName: serviceOrder?.contactInformation?.name,
      driverEmail: serviceOrder?.contactInformation?.email,
      driverPhone: {
        countryCode:
          (serviceOrder?.contactInformation?.phoneNumber?.countryCode as Alpha3CountryCode) ??
          'CZE',
        number: serviceOrder?.contactInformation?.phoneNumber?.number ?? '',
        prefix: serviceOrder?.contactInformation?.phoneNumber?.prefix ?? '',
      },
      driverIdentityCard: serviceOrder?.contactInformation?.identityCard,
      cancelReason: serviceOrder?.cancelReason,
    };

    const isLoading =
      isOrderGroupsLoading ||
      isServiceCentresLoading ||
      isOrderVariantAccountingFetching ||
      isServiceOrderFetching ||
      isServiceOrderInsuranceFetching ||
      isServiceOrderVariantMandatoryFieldsLoading ||
      isOrderVariantReportingFetching ||
      isInsuranceTypesLoading;

    const isError =
      isOrderGroupsError ||
      isServiceCentresError ||
      isOrderVariantAccountingError ||
      isServiceOrderError ||
      isServiceOrderInsuranceError ||
      isServiceOrderVariantMandatoryFieldsError ||
      isOrderVariantReportingError ||
      isInsuranceTypesError;

    return (
      <DataStatus isLoading={isLoading} isError={isError} minHeight={144}>
        <Form<OrderInsuranceFormType>
          onSubmit={onSubmit}
          experimentalZodSchema={orderInsuranceFormSchema(isFieldRequired)}
          defaultValues={defaultValues}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi) => {
            // Store formApi in ref
            formApiRef.current = formApi;

            return (
              <>
                <FormField control={control} name="contactPerson" type="hidden" />
                <VStack spacing={4}>
                  <Card
                    variant="inlineGrey"
                    title={i18n.t('general.labels.basicInformation')}
                    hasSeparator
                  >
                    <VStack spacing={4}>
                      <Grid columns={[1, 1, 1, 8]} spacing={4}>
                        <GridItem>
                          <FormField
                            control={control}
                            name="completionAtDate"
                            type="date"
                            relativeDates={relativeDates}
                            label={i18n.t('entity.order.labels.completionDate')}
                            isDisabled={props.isReadOnly}
                            isRequired={isFieldRequired('completionAtDate')}
                            data-testid={suffixTestId('completionAtDate', props)}
                          />
                        </GridItem>
                        <GridItem>
                          <FormField
                            control={control}
                            name="completionAtTime"
                            type="time"
                            label={i18n.t('entity.order.labels.completionTime')}
                            isDisabled={props.isReadOnly}
                            isRequired={isFieldRequired('completionAtTime')}
                            data-testid={suffixTestId('completionAtTime', props)}
                          />
                        </GridItem>
                        <GridItem>
                          <FormField
                            control={control}
                            name="totalPriceEstimated"
                            type="number"
                            label={i18n.t('entity.order.labels.estimatedPrice')}
                            isDisabled={props.isReadOnly}
                            isRequired={isFieldRequired('totalPriceEstimated')}
                            data-testid={suffixTestId('totalPriceEstimated', props)}
                            suffix={formatCurrencySymbol(tenantCurrency)}
                          />
                        </GridItem>
                        <GridItem>
                          <FormField
                            label={i18n.t('entity.vehicle.labels.actualMileage')}
                            name="mileage"
                            type="integer"
                            control={control}
                            minStepperValue={0}
                            isDisabled={props.isReadOnly}
                            isRequired={isFieldRequired('mileage')}
                            data-testid={suffixTestId('mileage', props)}
                            suffix={i18n.t('general.metric.km')}
                          />
                        </GridItem>
                        <GridItem span={[1, 1, 1, 4]}>
                          <Box flex={1} width="100%">
                            <FuelLevelInput
                              name="fuelTank"
                              control={control}
                              isDisabled={props.isReadOnly}
                              isRequired={isFieldRequired('fuelTank')}
                              data-testid={suffixTestId('fuelTank', props)}
                            />
                          </Box>
                        </GridItem>
                        <GridItem span={[1, 1, 1, 2]}>
                          <FormField
                            control={control}
                            name="serviceCenterId"
                            type="choice"
                            options={filteredServiceCentresOptions}
                            isLoading={isLoading}
                            label={i18n.t('entity.order.labels.serviceCenter')}
                            isDisabled={
                              props.isReadOnly ||
                              orderVariantAccounting?.isDefaultServiceCenterLocked
                            }
                            isRequired={isFieldRequired('serviceCenterId')}
                            isNotClearable
                            data-testid={suffixTestId('serviceCenter', props)}
                          />
                        </GridItem>
                        <GridItem span={[1, 1, 1, 2]}>
                          <FormField
                            control={control}
                            name="serviceOrderGroupId"
                            type="choice"
                            options={filteredOrderGroupsOptions}
                            isLoading={isLoading}
                            label={i18n.t('entity.order.labels.serviceOrderGroup')}
                            isDisabled={
                              props.isReadOnly ||
                              orderVariantReporting?.isDefaultServiceOrderGroupLocked
                            }
                            isRequired={isFieldRequired('serviceOrderGroupId')}
                            isNotClearable
                            data-testid={suffixTestId('serviceOrderGroup', props)}
                          />
                        </GridItem>
                      </Grid>
                      <Separator spacing={0} />
                      <Grid columns={4} spacing={4}>
                        <FormField
                          control={control}
                          name="insuranceCompany"
                          type="choice"
                          label={i18n.t('entity.order.labels.insuranceCompany')}
                          isDisabled={props.isReadOnly}
                          options={insuranceCompaniesOptions}
                          isLoading={isInsuranceCompaniesLoading}
                          isRequired={isFieldRequired('insuranceCompany')}
                          data-testid={suffixTestId('insuranceCompany', props)}
                        />
                        <FormField
                          control={control}
                          name="insuranceTypeId"
                          type="choice"
                          label={i18n.t('entity.order.labels.insuranceType')}
                          isDisabled={props.isReadOnly}
                          options={insuranceTypesOptions}
                          isLoading={isInsuranceTypesLoading}
                          isRequired={isFieldRequired('insuranceType')}
                          data-testid={suffixTestId('insuranceType', props)}
                        />
                        <FormField
                          control={control}
                          name="insuranceEventNumber"
                          type="text"
                          label={i18n.t('entity.order.labels.insuranceEventNumber')}
                          isDisabled={props.isReadOnly}
                          isRequired={isFieldRequired('insuranceEventNumber')}
                          data-testid={suffixTestId('insuranceEventNumber', props)}
                        />
                        <FormField
                          control={control}
                          name="insuranceClaimNumber"
                          type="text"
                          label={i18n.t('entity.order.labels.insuranceClaimNumber')}
                          isDisabled={props.isReadOnly}
                          isRequired={isFieldRequired('insuranceClaimNumber')}
                          data-testid={suffixTestId('insuranceClaimNumber', props)}
                        />
                        <FormField
                          control={control}
                          name="insuranceClaimOccurrenceAt"
                          type="apiDate"
                          label={i18n.t('entity.order.labels.insuranceClaimOccurrenceAt')}
                          isDisabled={props.isReadOnly}
                          isRequired={isFieldRequired('insuranceClaimOccurrenceAt')}
                          data-testid={suffixTestId('insuranceClaimOccurrenceAt', props)}
                        />

                        <Box paddingTop={6}>
                          <FormField
                            control={control}
                            name="initialInspectionPerformed"
                            type="checkbox"
                            label={i18n.t('entity.order.labels.initialInspectionPerformed')}
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('initialInspectionPerformed', props)}
                          />
                        </Box>
                        <Box paddingTop={6}>
                          <FormField
                            control={control}
                            name="vatDeducatible"
                            type="checkbox"
                            label={i18n.t('entity.order.labels.vatDeducatible')}
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('vatDeducatible', props)}
                          />
                        </Box>
                        <Box paddingTop={6}>
                          <FormField
                            control={control}
                            name="unblockingOfPayment"
                            type="checkbox"
                            label={i18n.t('entity.order.labels.unblockingOfPayment')}
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('unblockingOfPayment', props)}
                          />
                        </Box>
                        <FormField
                          control={control}
                          name="coverLetter"
                          type="checkbox"
                          label={i18n.t('entity.order.labels.coverLetter')}
                          isDisabled={props.isReadOnly}
                          data-testid={suffixTestId('coverLetter', props)}
                        />
                        <FormField
                          control={control}
                          name="returnUsedPart"
                          type="checkbox"
                          label={i18n.t('entity.order.labels.returnUsedParts')}
                          isDisabled={props.isReadOnly}
                          data-testid={suffixTestId('returnUsedPart', props)}
                        />
                      </Grid>
                      <Separator spacing={0} />
                      <Grid columns={2}>
                        <VStack spacing={1}>
                          <HStack justify="space-between" align="flex-end">
                            <Label>{i18n.t('general.labels.note')}</Label>
                            <PredefinedNotes
                              note={formApi.watch('note') ?? null}
                              onPrefill={(note) => formApi.setValue('note', note)}
                              resource="SERVICE_CASE"
                              context="service_order"
                              isLinkVariant
                              data-testid={suffixTestId('predefinedNotes', props)}
                            />
                          </HStack>
                          <FormField
                            control={control}
                            name="note"
                            type="textarea"
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('note', props)}
                          />
                        </VStack>
                        <VStack spacing={1}>
                          <HStack justify="space-between" align="flex-end">
                            <Label>{i18n.t('entity.order.labels.reasonForCancellation')}</Label>
                            <PredefinedNotes
                              note={formApi.watch('cancelReason') ?? null}
                              onPrefill={(note) => formApi.setValue('cancelReason', note)}
                              context="cancel_reason"
                              resource="SERVICE_ORDER"
                              isLinkVariant
                              data-testid={suffixTestId('predefinedNotesCancelReason', props)}
                            />
                          </HStack>
                          <FormField
                            control={control}
                            name="cancelReason"
                            type="textarea"
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('cancelReason', props)}
                          />
                        </VStack>
                      </Grid>
                    </VStack>
                  </Card>
                  <DriverInformationSection
                    control={control}
                    formApi={formApi}
                    countriesOptions={countriesOptions}
                    phoneInputKey={phoneInputKey}
                    refreshPhoneInputKey={refreshPhoneInputKey}
                    isReadOnly={props.isReadOnly}
                    data-testid={suffixTestId('driverInformation', props)}
                  />
                  <ButtonGroup align="right">
                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.saveChanges')}
                      isDisabled={props.isReadOnly}
                      data-testid={suffixTestId('save', props)}
                    />
                  </ButtonGroup>
                </VStack>
              </>
            );
          }}
        </Form>
      </DataStatus>
    );
  }
);
